import React, { useState, useEffect, useRef, useCallback } from 'react';

/**
 * Frecuencias de tonos para los 4 pads (Escala pentatónica mayor brillante)
 */
const PAD_TONES = [
    { id: 0, name: 'VERDE', color: '#10b981', activeColor: '#34d399', glow: 'rgba(16, 185, 129, 0.8)', freq: 329.63 }, // E4
    { id: 1, name: 'ROJO', color: '#ef4444', activeColor: '#f87171', glow: 'rgba(239, 68, 68, 0.8)', freq: 261.63 },   // C4
    { id: 2, name: 'AMARILLO', color: '#f59e0b', activeColor: '#fbbf24', glow: 'rgba(245, 158, 11, 0.8)', freq: 392.00 }, // G4
    { id: 3, name: 'AZUL', color: '#3b82f6', activeColor: '#60a5fa', glow: 'rgba(59, 130, 246, 0.8)', freq: 523.25 }   // C5
];

const DEFAULT_TARGET_STEPS = 6; // Por defecto 6 pasos para la partida

export const MvpXolosZoneSequence = ({ onGameFinish, targetRounds = DEFAULT_TARGET_STEPS }) => {
    const targetSteps = parseInt(targetRounds, 10) > 0 ? parseInt(targetRounds, 10) : DEFAULT_TARGET_STEPS;

    // Fases: 'instructions' | 'countdown' | 'demonstrating' | 'player_turn' | 'game_over' | 'finished'
    const [gameState, setGameState] = useState('instructions');
    const [countdown, setCountdown] = useState(3);
    const [sequence, setSequence] = useState([]);
    const [sequenceNames, setSequenceNames] = useState([]);
    const [playerInputs, setPlayerInputs] = useState([]);
    const [playerStep, setPlayerStep] = useState(0);
    const [activePad, setActivePad] = useState(null);
    const [statusMessage, setStatusMessage] = useState('¡Prepárate!');

    const audioCtxRef = useRef(null);
    const isInteractingRef = useRef(false);

    // Inicializar Web Audio API context
    const getAudioContext = useCallback(() => {
        if (!audioCtxRef.current) {
            const AudioContext = window.AudioContext || window.webkitAudioContext;
            if (AudioContext) {
                audioCtxRef.current = new AudioContext();
            }
        }
        if (audioCtxRef.current && audioCtxRef.current.state === 'suspended') {
            audioCtxRef.current.resume();
        }
        return audioCtxRef.current;
    }, []);

    // Reproducir tono para un pad
    const playTone = useCallback((padId, durationMs = 280) => {
        try {
            const ctx = getAudioContext();
            if (!ctx) return;

            const pad = PAD_TONES[padId];
            if (!pad) return;

            const osc = ctx.createOscillator();
            const gain = ctx.createGain();

            osc.type = 'triangle';
            osc.frequency.setValueAtTime(pad.freq, ctx.currentTime);

            gain.gain.setValueAtTime(0.35, ctx.currentTime);
            gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + (durationMs / 1000));

            osc.connect(gain);
            gain.connect(ctx.destination);

            osc.start();
            osc.stop(ctx.currentTime + (durationMs / 1000));
        } catch (e) {
            console.warn("Audio synth warning:", e);
        }
    }, [getAudioContext]);

    // Reproducir sonido de error
    const playErrorTone = useCallback(() => {
        try {
            const ctx = getAudioContext();
            if (!ctx) return;
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.type = 'sawtooth';
            osc.frequency.setValueAtTime(120, ctx.currentTime);
            gain.gain.setValueAtTime(0.4, ctx.currentTime);
            gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.4);
            osc.connect(gain);
            gain.connect(ctx.destination);
            osc.start();
            osc.stop(ctx.currentTime + 0.4);
        } catch (e) {}
    }, [getAudioContext]);

    // Reproducir fanfarria de victoria
    const playVictoryChime = useCallback(() => {
        try {
            const ctx = getAudioContext();
            if (!ctx) return;
            const notes = [261.63, 329.63, 392.00, 523.25, 659.25];
            notes.forEach((freq, idx) => {
                const osc = ctx.createOscillator();
                const gain = ctx.createGain();
                osc.type = 'sine';
                osc.frequency.setValueAtTime(freq, ctx.currentTime + idx * 0.09);
                gain.gain.setValueAtTime(0.3, ctx.currentTime + idx * 0.09);
                gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + idx * 0.09 + 0.3);
                osc.connect(gain);
                gain.connect(ctx.destination);
                osc.start(ctx.currentTime + idx * 0.09);
                osc.stop(ctx.currentTime + idx * 0.09 + 0.35);
            });
        } catch (e) {}
    }, [getAudioContext]);

    // Iniciar cuenta regresiva al presionar botón de instrucciones
    const handleStartClick = () => {
        getAudioContext(); // Desbloquear audio en interacción de usuario
        setGameState('countdown');
        setCountdown(3);
    };

    // Cuenta regresiva
    useEffect(() => {
        if (gameState === 'countdown') {
            const timer = setInterval(() => {
                setCountdown((prev) => {
                    if (prev <= 1) {
                        clearInterval(timer);
                        startSingleMatch();
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
            return () => clearInterval(timer);
        }
    }, [gameState]);

    // Iniciar partida única de N pasos
    const startSingleMatch = () => {
        const fullSequence = [];
        for (let i = 0; i < targetSteps; i++) {
            fullSequence.push(Math.floor(Math.random() * 4));
        }
        const names = fullSequence.map(id => PAD_TONES[id].name);

        setSequence(fullSequence);
        setSequenceNames(names);
        setPlayerInputs([]);
        setPlayerStep(0);
        demonstrateSequence(fullSequence);
    };

    // Demostrar secuencia completa al aficionado
    const demonstrateSequence = (seqToPlay) => {
        setGameState('demonstrating');
        setStatusMessage(`👀 ¡Observa la secuencia de ${seqToPlay.length} pasos!`);
        setActivePad(null);

        let idx = 0;
        const interval = setInterval(() => {
            if (idx < seqToPlay.length) {
                const currentPad = seqToPlay[idx];
                setActivePad(currentPad);
                playTone(currentPad, 320);

                setTimeout(() => {
                    setActivePad(null);
                }, 340);

                idx++;
            } else {
                clearInterval(interval);
                setTimeout(() => {
                    setGameState('player_turn');
                    setPlayerStep(0);
                    setStatusMessage(`🎯 ¡Tu turno! Repite los ${seqToPlay.length} pasos en orden`);
                }, 450);
            }
        }, 680);
    };

    // Manejar toque del aficionado en un pad
    const handlePadClick = (padId) => {
        if (gameState !== 'player_turn' || isInteractingRef.current) return;

        isInteractingRef.current = true;
        setActivePad(padId);
        playTone(padId, 250);

        const padName = PAD_TONES[padId].name;
        const updatedInputs = [...playerInputs, padName];
        setPlayerInputs(updatedInputs);

        // Feedback háptico nativo en móviles
        if (navigator.vibrate) {
            try { navigator.vibrate(35); } catch (e) {}
        }

        setTimeout(() => {
            setActivePad(null);
            isInteractingRef.current = false;
        }, 180);

        const expectedPad = sequence[playerStep];

        if (padId === expectedPad) {
            const nextStep = playerStep + 1;

            if (nextStep === sequence.length) {
                // ¡Partida completada con éxito perfecto! (6 de 6)
                setGameState('finished');
                setStatusMessage('🎉 ¡Secuencia perfecta! ¡Has ganado!');
                playVictoryChime();

                setTimeout(() => {
                    if (onGameFinish) {
                        onGameFinish({
                            gameType: 'SEQUENCE',
                            success: true,
                            score: targetSteps,
                            stepsCorrect: targetSteps,
                            totalSteps: targetSteps,
                            sequenceShown: sequenceNames,
                            sequenceInput: updatedInputs
                        });
                    }
                }, 1300);
            } else {
                setPlayerStep(nextStep);
            }
        } else {
            // Error en la secuencia -> Finalizar partida
            setGameState('game_over');
            setStatusMessage(`❌ ¡Te equivocaste! Lograste ${playerStep} de ${targetSteps} pasos`);
            playErrorTone();

            setTimeout(() => {
                if (onGameFinish) {
                    onGameFinish({
                        gameType: 'SEQUENCE',
                        success: false,
                        score: playerStep,
                        stepsCorrect: playerStep,
                        totalSteps: targetSteps,
                        sequenceShown: sequenceNames,
                        sequenceInput: updatedInputs
                    });
                }
            }, 1500);
        }
    };

    return (
        <div className="MvpXolosZoneSequence w-100 d-flex flex-column align-items-center justify-content-center p-3 text-white" style={{ minHeight: '65vh', userSelect: 'none' }}>
            
            {/* 1. Pantalla de Instrucciones Previas (Estilo Grito Mexicano) */}
            {gameState === 'instructions' ? (
                <div className="w-100 animate__animated animate__fadeIn" style={{ maxWidth: '420px' }}>
                    <div className="card border-0 shadow-lg rounded-4 p-4 text-center" style={{ background: 'linear-gradient(145deg, #1f1f23 0%, #111113 100%)', border: '1px solid rgba(239, 68, 68, 0.3)' }}>
                        <div className="d-flex justify-content-center mb-3">
                            <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: '76px', height: '76px', background: 'radial-gradient(circle, #ef4444 0%, #991b1b 100%)', boxShadow: '0 0 25px rgba(239, 68, 68, 0.6)' }}>
                                <i className="bi bi-music-note-beamed fs-1 text-white"></i>
                            </div>
                        </div>

                        <h4 className="fw-black text-white text-uppercase mb-2" style={{ letterSpacing: '1px' }}>
                            ¡Repite la secuencia!
                        </h4>
                        <p className="text-white-50 small mb-4">
                            Pon a prueba tu memoria visual
                        </p>

                        <div className="bg-dark bg-opacity-75 rounded-3 p-3 text-start mb-4 border border-secondary border-opacity-25">
                            <div className="d-flex align-items-start gap-2.5 mb-2.5">
                                <span className="badge bg-danger rounded-circle p-1 px-2 fw-bold">1</span>
                                <small className="text-white"><strong>Observa el patrón:</strong> Se iluminarán {targetSteps} botones de colores en orden aleatorio.</small>
                            </div>
                            {/* <div className="d-flex align-items-start gap-2.5 mb-2.5">
                                <span className="badge bg-danger rounded-circle p-1 px-2 fw-bold">2</span>
                                <small className="text-white"><strong>Escucha el tono:</strong> Cada botón emite una nota musical única.</small>
                            </div> */}
                            <div className="d-flex align-items-start gap-2.5 mb-2.5">
                                <span className="badge bg-danger rounded-circle p-1 px-2 fw-bold">2</span>
                                <small className="text-white"><strong>Repite la secuencia:</strong> Toca los botones en el orden exacto para repetir la secuencia que se te mostró.</small>
                            </div>
                            <div className="d-flex align-items-start gap-2.5">
                                <span className="badge bg-warning text-dark rounded-circle p-1 px-2 fw-bold">3</span>
                                <small className="text-warning fw-semibold"><strong>¡Gana tu pase exclusivo!:</strong> Si aciertas los {targetSteps} pasos completos de la secuencia, podrías ganar tu pase Exclusivo al 'MVP Xolos Femenil'.</small>
                            </div>
                            <div className="d-flex align-items-start gap-2.5">
                                <small className="text-white">Los pases son limitados, así que participa ya mismo.</small>
                            </div>
                        </div>

                        <button
                            type="button"
                            onClick={handleStartClick}
                            className="btn btn-danger btn-lg w-100 fw-bold py-3 rounded-3 shadow text-uppercase d-flex align-items-center justify-content-center gap-2"
                            style={{
                                background: 'linear-gradient(90deg, #ef4444 0%, #b91c1c 100%)',
                                letterSpacing: '1px',
                                boxShadow: '0 4px 20px rgba(239, 68, 68, 0.5)'
                            }}
                        >
                            <i className="bi bi-play-circle-fill fs-5"></i>
                            Iniciar ahora
                        </button>
                    </div>
                </div>
            ) : (
                <>
                    {/* Header del Minijuego */}
                    <div className="text-center mb-3">
                        <div className="badge bg-danger px-3 py-2 fs-6 text-uppercase fw-bold shadow-sm mb-2">
                            <i className="bi bi-music-note-beamed me-2"></i>Repite la Secuencia ({targetSteps} Pasos)
                        </div>
                        <h4 className="fw-bold text-white mb-1" style={{ textShadow: '0 2px 8px rgba(0,0,0,0.8)' }}>
                            {statusMessage}
                        </h4>
                        {gameState !== 'countdown' && (
                            <div className="d-flex justify-content-center align-items-center gap-2 mt-2">
                                <span className="small text-white-50 fw-semibold">Progreso:</span>
                                <div className="d-flex gap-1">
                                    {Array.from({ length: targetSteps }).map((_, rIdx) => (
                                        <div
                                            key={rIdx}
                                            style={{
                                                width: '24px',
                                                height: '8px',
                                                borderRadius: '4px',
                                                background: rIdx < playerStep ? '#10b981' : (gameState === 'demonstrating' ? '#ef4444' : 'rgba(255,255,255,0.2)'),
                                                transition: 'all 0.3s ease'
                                            }}
                                        />
                                    ))}
                                </div>
                                <span className="badge bg-dark border border-secondary text-white small ms-1">
                                    {playerStep}/{targetSteps}
                                </span>
                            </div>
                        )}
                    </div>

                    {/* Pantalla de Cuenta Regresiva */}
                    {gameState === 'countdown' ? (
                        <div className="d-flex flex-column align-items-center justify-content-center py-5">
                            <div
                                className="d-flex align-items-center justify-content-center rounded-circle border border-4 border-danger shadow-lg animate__animated animate__pulse animate__infinite"
                                style={{
                                    width: '130px',
                                    height: '130px',
                                    background: 'radial-gradient(circle, #ef4444 0%, #7f1d1d 100%)',
                                    boxShadow: '0 0 35px rgba(239, 68, 68, 0.8)'
                                }}
                            >
                                <span className="fw-black text-white" style={{ fontSize: '4.5rem', fontWeight: 900 }}>
                                    {countdown}
                                </span>
                            </div>
                            <p className="mt-4 text-white-50 small fw-semibold text-center">
                                Sigue la secuencia de luces y reproduce el patrón tocando los botones.
                            </p>
                        </div>
                    ) : (
                        /* Tablero 2x2 de Pads Luminosos */
                        <div
                            className="position-relative p-3 rounded-4 shadow-lg border border-secondary"
                            style={{
                                background: 'linear-gradient(145deg, #18181b 0%, #09090b 100%)',
                                maxWidth: '340px',
                                width: '100%',
                                aspectRatio: '1/1',
                                display: 'grid',
                                gridTemplateColumns: '1fr 1fr',
                                gridTemplateRows: '1fr 1fr',
                                gap: '14px'
                            }}
                        >
                            {PAD_TONES.map((pad) => {
                                const isLit = activePad === pad.id;
                                return (
                                    <button
                                        key={pad.id}
                                        type="button"
                                        disabled={gameState !== 'player_turn'}
                                        onClick={() => handlePadClick(pad.id)}
                                        className="btn border-0 rounded-4 d-flex flex-column align-items-center justify-content-center position-relative overflow-hidden"
                                        style={{
                                            background: isLit ? pad.activeColor : pad.color,
                                            opacity: gameState === 'demonstrating' && !isLit ? 0.35 : 1,
                                            transform: isLit ? 'scale(0.96)' : 'scale(1)',
                                            boxShadow: isLit
                                                ? `0 0 25px ${pad.glow}, inset 0 0 15px rgba(255,255,255,0.7)`
                                                : '0 6px 16px rgba(0,0,0,0.6), inset 0 2px 4px rgba(255,255,255,0.3)',
                                            transition: 'all 0.12s cubic-bezier(0.4, 0, 0.2, 1)',
                                            cursor: gameState === 'player_turn' ? 'pointer' : 'default',
                                            outline: 'none'
                                        }}
                                    >
                                        <div
                                            className="rounded-circle d-flex align-items-center justify-content-center"
                                            style={{
                                                width: '42px',
                                                height: '42px',
                                                background: isLit ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.25)',
                                                color: isLit ? '#18181b' : '#ffffff',
                                                transition: 'all 0.12s ease'
                                            }}
                                        >
                                            <i className={`bi ${pad.id === 0 ? 'bi-circle-fill' : pad.id === 1 ? 'bi-square-fill' : pad.id === 2 ? 'bi-triangle-fill' : 'bi-diamond-fill'} fs-5`}></i>
                                        </div>
                                    </button>
                                );
                            })}

                            {/* Logo central o indicador */}
                            <div
                                className="position-absolute top-50 start-50 translate-middle rounded-circle d-flex align-items-center justify-content-center shadow-lg border border-dark"
                                style={{
                                    width: '64px',
                                    height: '64px',
                                    background: '#000000',
                                    pointerEvents: 'none',
                                    boxShadow: '0 0 15px rgba(0,0,0,0.9)'
                                }}
                            >
                                <i className="bi bi-controller text-danger fs-3"></i>
                            </div>
                        </div>
                    )}

                    {/* Mensaje de estado inferior */}
                    {gameState === 'player_turn' && (
                        <div className="mt-3 text-center animate__animated animate__fadeIn">
                            <span className="badge bg-success-subtle text-success px-3 py-1 rounded-pill small fw-bold">
                                <i className="bi bi-hand-index-thumb me-1"></i>Toca los botones en orden ({playerStep}/{targetSteps})
                            </span>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default MvpXolosZoneSequence;
